import {
  Cart,
  DeepPartial,
  LineItem,
  Order,
  ShippingMethod,
  ShippingStatus,
  WorkList,
  WorkListGroup,
  WorkListStatus,
} from "@/utils/types/types";
import { Prisma, WebUser, WorklistColumns } from "@prisma/client";
import axios from "axios";
import Cookies from "js-cookie";

export const Api = axios.create({
  baseURL: process.env.REACT_APP_API,
  // 20s (was 10s, raised 2026-08-16): live backend intermittently stalls 7-9.5s
  // on stale-Mongo-connection rebuild after instance freeze/thaw (~4x/day, see
  // 08-16 finding). At 10s the client gave up while the server completed the
  // work - weekend signups "failed" but created accounts. 20s clears the
  // observed worst case; real fix is backend redial tuning riding with this.
  timeout: 20000,
});

export const Crud = {
  users: crud<WebUser, Prisma.WebUserWhereInput>("/api/users"),
  lineItems: crud<LineItem, Prisma.LineItemsWhereInput>("/api/line-items"),
  carts: {
    ...crud<Cart, Prisma.CartWhereInput>("/api/carts"),
    count() {
      return Api.get<{ count: number }>("/api/carts/count").then(
        res => res.data
      );
    },
    // Bulk delete (2026-09-24): one call for the Carts page's "Delete selected".
    deleteMany(ids: string[]) {
      return Api.post<{ ok: boolean; deleted: number }>(
        "/api/carts/delete-many",
        { ids }
      ).then(res => res.data);
    },
    // Short share link (2026-09-03): admin mints shareSecret + shortCode
    // server-side and gets back the /c/<code> URL (6 random chars — no
    // customer name in the link). Optional parentOrderNo rides along.
    async shareLink(id: string, parentOrderNo?: string | null) {
      const { data } = await Api.post<{
        cartId: string;
        shareSecret: string;
        shortCode: string;
      }>(`/api/carts/${id}/share-link`);
      const appUrl =
        process.env.REACT_APP_FRONTEND_URL || window.location.origin;
      const suffix = parentOrderNo
        ? `?parentOrderNo=${encodeURIComponent(parentOrderNo)}`
        : "";
      return {
        ...data,
        url: `${appUrl}/c/${data.shortCode}${suffix}`,
      };
    },
    resolveShortCode(code: string) {
      return Api.get<{ cartId: string; secret: string }>(
        `/api/carts/short/${encodeURIComponent(code)}`
      ).then(res => res.data);
    },
    addLineItem(
      id: string,
      payload: Partial<LineItem> & { itemJson: LineItem["itemJson"] }
    ) {
      return Api.post<{ ok: boolean; lineItem: LineItem }>(
        `/api/carts/${id}/line-items`,
        payload
      ).then(res => res.data);
    },
    updateLineItem(id: string, lineItemId: string, payload: Partial<LineItem>) {
      return Api.post<{ ok: boolean }>(
        `/api/carts/${id}/line-items/${lineItemId}/update`,
        payload
      ).then(res => res.data);
    },
    removeLineItem(
      id: string,
      lineItemId: string,
      options?: { shareSecret?: string }
    ) {
      return Api.post<{ ok: boolean }>(
        `/api/carts/${id}/line-items/${lineItemId}/remove`,
        options || {}
      ).then(res => res.data);
    },
  },
  orders: crud<Order, Prisma.OrdersWhereInput>("/api/orders"),
  worklists: crud<WorkList, Prisma.WorkListWhereInput>("/api/worklists"),
  worklistGroups: crud<WorkListGroup, Prisma.WorkListGroupWhereInput>(
    "/api/worklist-groups"
  ),
  worklistStatuses: crud<WorkListStatus, Prisma.WorkListStatusWhereInput>(
    "/api/worklist-statuses"
  ),
  shippingMethods: crud<ShippingMethod, Prisma.ShippingMethodWhereInput>(
    "/api/shipping-methods"
  ),
  shippingStatuses: crud<ShippingStatus, Prisma.ShippingStatusWhereInput>(
    "/api/shipping-statuses"
  ),
  worklistColumns: crud<WorklistColumns, Prisma.WorklistColumnsWhereInput>(
    "/api/worklist-columns"
  ),
};

Api.interceptors.request.use(config => {
  const authToken = Cookies.get("stytch_session_jwt");
  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`;
    config.headers["x-uid"] = Cookies.get("stytch_user_id");
  } else {
    config.headers["x-guid"] = Cookies.get("guest_user_id");
  }
  return config;
});

/**
 * Attach the failing endpoint to the error so Sentry can tag and group by it.
 *
 * WHY: axios failures reach Sentry as unhandled rejections thrown from inside
 * axios internals, so the stack is always `request.ontimeout(axios/lib/...)`
 * and the `url` tag is the PAGE, not the call. That is why TPW-ASTRO-WEB-UI-26
 * (10s timeouts, 136 events / 45 users, 64% on /upload, 24 days) could never be
 * traced to an endpoint. Read by the beforeSend hook in public/index.html.
 *
 * Deliberately does NOT modify err.message. DashboardOrders.tsx does an exact
 * `err?.message === "Network Error"` comparison and appending would silently
 * break its offline detection. Two other call sites use .includes("timeout"),
 * which would survive, but the exact match does not.
 *
 * Query strings are stripped: they carry ids and emails, and would fragment
 * grouping into one issue per request.
 */
Api.interceptors.response.use(
  response => response,
  error => {
    try {
      const cfg = error?.config;
      if (cfg) {
        const method = String(cfg.method || "get").toUpperCase();
        const path = String(cfg.url || "").split("?")[0];
        error.tpwEndpoint = `${method} ${path}`;
      }
    } catch {
      // diagnostics must never break a request
    }
    return Promise.reject(error);
  }
);

function crud<T, Where = DeepPartial<T>>(path: string) {
  return {
    get(id: string, find?: Where) {
      const url = find
        ? `${path}/${id}?${new URLSearchParams({ where: JSON.stringify(find) }).toString()}`
        : `${path}/${id}`;

      return Api.get<T>(url).then(res => res.data);
    },
    getAll(
      find?: Where &
        Partial<{ pagination: { page?: number; pageSize?: number } }>
    ) {
      // Caller controls paging: single request, exactly as before.
      if (find && "pagination" in find) {
        const url = `${path}?${new URLSearchParams({ where: JSON.stringify(find) }).toString()}`;
        return Api.get<T[]>(url).then(res => res.data);
      }

      // No explicit pagination: fetch in 200-row chunks and concatenate.
      // Netlify caps synchronous function responses at 6MB; unpaged worklist
      // fetches grew past it (413 in function logs) and took the Archive page
      // down. The backend reads pagination out of the `where` JSON as
      // skip/take. Sequential, not parallel — the page count is unknown until
      // a short page arrives.
      const pageSize = 200;
      const fetchPage = (page: number) => {
        const where = JSON.stringify({
          ...(find ?? {}),
          pagination: { page, pageSize },
        });
        const url = `${path}?${new URLSearchParams({ where }).toString()}`;
        return Api.get<T[]>(url).then(res => res.data);
      };

      return (async () => {
        const all: T[] = [];
        for (let page = 1; ; page++) {
          const rows = await fetchPage(page);
          all.push(...rows);
          if (rows.length < pageSize) break;
        }
        return all;
      })();
    },
    create(payload: DeepPartial<T>) {
      return Api.post<T>(path, payload).then(res => res.data);
    },
    update(id: string, payload: DeepPartial<T>) {
      return Api.patch<T>(`${path}/${id}`, payload).then(res => res.data);
    },
    delete(id: string) {
      return Api.delete(`${path}/${id}`).then(res => res.data);
    },
  };
}
